/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useId } from 'react';
import { Form as ReactFinalForm } from 'react-final-form';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import FirstNameField from './FirstNameField';
import MiddleNameField from './MiddleNameField';
import LastNameField from './LastNameField';
import AddressField from './AddressField';
import CityField from './CityField';
import StateField from './StateField';
import ZipCodeField from './ZipCodeField';
import DateOfBirthField from './DateOfBirthField';
import PhoneNumberField from './PhoneNumberField';
import SocialSecurityNumberField from './SocialSecurityNumberField';
import {
  Button,
  Form,
  Grid,
  Heading,
  Paragraph,
  Popover,
  Popper,
  TextButton,
  VisuallyHidden,
} from '@zillow/constellation';
import {
  useIdVerificationCallbacks,
  usePassiveAuthData,
  usePidAnswers,
  useSetPidAnswers,
  useSkipTermsStep,
  useTermSet,
} from '../../contexts/IdVerificationContext';
import { IdvStep, TermSet } from '../../../..';
import { getWizardOnSubmit } from '../Content.utils';
import PrivacyCard from '../../PrivacyCard';
import { useEffectOnce } from 'react-use';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../clickstream/events';

const renterTemplateAreaXl = [
  'head head head head head head privacy privacy privacy privacy privacy',
  'subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy',
  'first first first first first first privacy privacy privacy privacy privacy',
  'middle middle middle middle middle middle privacy privacy privacy privacy privacy',
  'last last last last last last privacy privacy privacy privacy privacy',
  'address address address address address address privacy privacy privacy privacy privacy',
  'city city state state zip zip privacy privacy privacy privacy privacy',
  'dob dob dob phone phone phone privacy privacy privacy privacy privacy',
  'ssn ssn ssn . . . privacy privacy privacy privacy privacy',
  'popper popper popper popper popper popper privacy privacy privacy privacy privacy',
];

const renterTemplateAreaLg = [
  'head head head head head head privacy privacy privacy privacy privacy',
  'subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy',
  'first first first first first first privacy privacy privacy privacy privacy',
  'middle middle middle middle middle middle privacy privacy privacy privacy privacy',
  'last last last last last last privacy privacy privacy privacy privacy',
  'address address address address address address privacy privacy privacy privacy privacy',
  'city   city  city  city  city  city  privacy privacy privacy privacy privacy',
  'state  state state state state state privacy privacy privacy privacy privacy',
  'zip    zip   zip   zip   zip   zip   privacy privacy privacy privacy privacy',
  'dob dob dob phone phone phone privacy privacy privacy privacy privacy',
  'ssn ssn ssn . . . privacy privacy privacy privacy privacy',
  'popper popper popper popper popper popper privacy privacy privacy privacy privacy',
];

const landlordTemplateArea = [
  'head head head head head head privacy privacy privacy privacy privacy',
  'subHead subHead subHead subHead subHead subHead privacy privacy privacy privacy privacy',
  'first first first first first first privacy privacy privacy privacy privacy',
  'middle middle middle middle middle middle privacy privacy privacy privacy privacy',
  'last last last last last last privacy privacy privacy privacy privacy',
  'address address address address address address privacy privacy privacy privacy privacy',
  'city city state state zip zip privacy privacy privacy privacy privacy',
  'ssn ssn ssn . . . privacy privacy privacy privacy privacy',
  'popper popper popper popper popper popper privacy privacy privacy privacy privacy',
];

const defaultRenterTemplateArea = [
  'head',
  'subHead',
  'first',
  'middle',
  'last',
  'address',
  'city',
  'state',
  'zip',
  'dob',
  'phone',
  'ssn',
  'popper',
  'privacy',
];

const defaultLandlordTemplateArea = [
  'head',
  'subHead',
  'first',
  'middle',
  'last',
  'address',
  'city',
  'state',
  'zip',
  'ssn',
  'popper',
  'privacy',
];

interface PersonalInfoStepProps {
  id: string;
  label: string;
}

export default function PersonalInfoStep({ id, label }: PersonalInfoStepProps) {
  const { onRequestStep } = useWizard();
  const idVerificationId = useId();
  const termSet = useTermSet();
  const skipTermsStep = useSkipTermsStep();
  const { jsc, hdimPayload } = usePassiveAuthData();
  const pidAnswers = usePidAnswers();
  const setPidAnswers = useSetPidAnswers();
  const { trackEvent } = useIdVerificationCallbacks();

  useEffectOnce(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('id_verification_provide_details'));
  });

  return (
    <ReactFinalForm
      initialValues={{
        ...pidAnswers,
        jsc,
        hdimPayload,
      }}
      onSubmit={async (formValues) => {
        setPidAnswers((currentPidAnswers) => ({
          ...currentPidAnswers,
          ...formValues,
          /*
            middleName and ssn are optional fields so they must be set to
            an empty string if their values are cleared out otherwise react-final-form
            will default them to 'undefined' resulting in their value never
            being updated. See the link below form more info:
            https://final-form.org/docs/final-form/faq#why-does-final-form-set-my--field-value-to-undefined
          */
          middleName: formValues?.middleName ?? '',
          ssn: formValues?.ssn ?? '',
        }));
      }}
      render={({ form, invalid }) => {
        return (
          <WizardStep
            id={id}
            label={label}
            meta={{
              isNextDisabled: invalid,
            }}
            onRequestNext={() => {
              trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('id_verification_provide_details'));
            }}
            onRequestPrev={(abortController: AbortController) => {
              const {
                firstName,
                middleName,
                lastName,
                currentAddress,
                currentCity,
                currentState,
                currentZip,
                phone,
                dob,
                ssn,
              } = form.getState().values;
              setPidAnswers((state) => ({
                ...state,
                firstName: firstName ?? '',
                middleName: middleName ?? '',
                lastName: lastName ?? '',
                currentAddress: currentAddress ?? '',
                currentCity: currentCity ?? '',
                currentState: currentState ?? '',
                currentZip: currentZip ?? '',
                phone: phone ?? '',
                dob: dob ?? '',
                ssn: ssn ?? '',
              }));

              if (skipTermsStep) {
                abortController.abort();
                onRequestStep(IdvStep.LandingStep);
              }
            }}
            onSubmit={getWizardOnSubmit(form)}
            render={({ handleSubmit }: { handleSubmit: any }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  <Grid
                    display="grid"
                    gridTemplateColumns={{
                      default: 'repeat(1, 1fr)',
                      lg: termSet === TermSet.RenterScreening ? 'repeat(11, 1fr)' : 'repeat(1, 1fr)',
                      xl: 'repeat(11, 1fr)',
                    }}
                    gap="md"
                    gridTemplateAreas={{
                      default:
                        termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : defaultRenterTemplateArea,
                      lg: termSet === TermSet.LandlordScreening ? defaultLandlordTemplateArea : renterTemplateAreaLg,
                      xl: termSet === TermSet.LandlordScreening ? landlordTemplateArea : renterTemplateAreaXl,
                    }}
                  >
                    <Grid gridArea="head">
                      <Heading level={4} as="h2">
                        Provide your details for verification
                      </Heading>
                    </Grid>
                    <Grid gridArea="subHead">
                      <Paragraph>
                        {termSet === TermSet.RenterScreening
                          ? 'This information will be sent to Experian and CIC to verify your identity and create credit and background reports.'
                          : 'This information helps our partners, Experian and CIC, verify your identity so you can review applicants’ information and find a suitable renter. '}
                      </Paragraph>
                    </Grid>
                    <Grid gridArea="first">
                      <FirstNameField />
                    </Grid>
                    <Grid gridArea="middle">
                      <MiddleNameField />
                    </Grid>
                    <Grid gridArea="last">
                      <LastNameField />
                    </Grid>
                    <Grid gridArea="address">
                      <AddressField />
                    </Grid>
                    <Grid gridArea="city">
                      <CityField />
                    </Grid>
                    <Grid gridArea="state">
                      <StateField />
                    </Grid>
                    <Grid gridArea="zip">
                      <ZipCodeField />
                    </Grid>
                    {termSet === TermSet.RenterScreening && (
                      <>
                        <Grid gridArea="dob">
                          <DateOfBirthField />
                        </Grid>
                        <Grid gridArea="phone">
                          <PhoneNumberField />
                        </Grid>
                      </>
                    )}
                    <Grid gridArea="ssn">
                      <SocialSecurityNumberField />
                    </Grid>
                    <Grid gridArea="privacy">
                      <PrivacyCard />
                    </Grid>
                    <Grid gridArea="popper">
                      <Popper
                        triggered={
                          <Popover
                            renderHeader={<Heading level={2}>Social Security number (SSN)</Heading>}
                            renderBody={
                              <Paragraph>
                                Providing a SSN helps verify your identity faster, but is not required. If this field is
                                left blank, Experian may need to ask additional questions to verify your identity.
                              </Paragraph>
                            }
                          />
                        }
                      >
                        <TextButton
                          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => e.preventDefault()}
                        >
                          Why do we ask for your Social Security number?
                        </TextButton>
                      </Popper>
                    </Grid>
                  </Grid>
                  <VisuallyHidden>
                    <Button type="submit" id={`${idVerificationId}-submit-pid`}>
                      Submit
                    </Button>
                  </VisuallyHidden>
                </Form>
              );
            }}
          />
        );
      }}
    />
  );
}
